import { Groups, InfoOutlined } from '@mui/icons-material';
import {
  Typography,
  ToggleButton,
  Grid,
  Stack,
  Chip,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  useTheme,
} from '@mui/material';
import { CheckboxSelect, NestedItem, Select, SelectOption, Switch } from '@perry-weather/component-library';
import { HistoricalReportSettings } from '../../../types/HistoricalReport';
import { TIME_PERIOD, MONTH_DAYS, WEEK_DAYS } from '../../../Constants/TimeConstants';
import { DEFAULT_MAX_CHIPS, FORMATS } from '../../../Constants/HistoricalReportConstants';

interface ExportSettingsProps {
  includeRecipients: boolean;
  recipients: NestedItem[];
  settings: HistoricalReportSettings;
  currentUserEmail: string | undefined;
  maxChips: number;
  recipientEmails: Map<string, string | null>;
  fileName: string;
  styles: {
    chip: any;
    select: any;
  };
  updateSettings: (updates: Partial<HistoricalReportSettings>) => void;
  updateMaxChips: (update: number) => void;
  handleRecipientsChange: (newUsers: string[]) => void;
  handleChangeFileName: (newFileName: string) => void;
  handleSelectedFormats: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ExportSettings = (props: ExportSettingsProps) => {
  const {
    includeRecipients,
    recipients,
    settings,
    currentUserEmail,
    maxChips,
    recipientEmails,
    fileName,
    styles,
    updateMaxChips,
    updateSettings,
    handleRecipientsChange,
    handleChangeFileName,
    handleSelectedFormats,
  } = props;

  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === 'dark';

  const timePeriods: SelectOption[] = TIME_PERIOD.map(x => {
    return { value: x.toUpperCase(), text: x };
  });
  const monthDays: SelectOption[] = MONTH_DAYS.map(x => {
    return { value: x.value, text: x.label };
  });
  const weekDays: SelectOption[] = WEEK_DAYS.map(x => {
    return { value: x.toUpperCase(), text: x };
  });

  return (
    <>
      <Typography variant='h6' fontWeight='bold'>
        Export Settings
      </Typography>
      <ToggleButton
        value={''}
        selected={includeRecipients}
        sx={{ ...styles.chip, gap: '8px' }}
        onClick={() => {
          updateSettings({ addedUsers: [], includeRecipients: !includeRecipients });
        }}>
        <Groups fontSize='small' /> Add Recipients
      </ToggleButton>
      {includeRecipients && (
        <Grid xs={12} md={9} xl={6}>
          <CheckboxSelect
            placementTop
            placeholder='Search Email Addresses'
            options={recipients}
            selections={settings.addedUsers}
            sx={{
              container: { width: '100%' },
              menu: { width: 'fit-content', textWrap: 'nowrap', paddingRight: 3 },
            }}
            onChange={handleRecipientsChange}
          />
        </Grid>
      )}
      <Stack direction='row' gap={3} flexWrap='wrap'>
        <Chip
          label={currentUserEmail}
          variant='outlined'
          sx={{
            ...styles.chip,
            paddingX: '0',
            fontWeight: '300',
            color: 'white',
          }}
        />
        {settings.addedUsers.slice(0, maxChips).map(recipient => (
          <Chip
            key={recipient}
            label={recipientEmails.get(recipient)}
            variant='outlined'
            sx={{
              ...styles.chip,
              paddingX: '0',
              fontWeight: '300',
              borderColor: isDarkTheme ? theme.palette.action.active : theme.palette.text.primary,
              backgroundColor: isDarkTheme ? theme.palette.background.disabled : theme.palette.border.input,
            }}
            onDelete={() => updateSettings({ addedUsers: settings.addedUsers.filter(x => x !== recipient) })}
          />
        ))}

        {settings.addedUsers.length > DEFAULT_MAX_CHIPS && (
          <Typography
            variant='body2'
            alignSelf={'center'}
            onClick={() => {
              updateMaxChips(maxChips === DEFAULT_MAX_CHIPS ? Infinity : DEFAULT_MAX_CHIPS);
            }}
            sx={{
              verticalAlign: 'center',
              cursor: 'pointer',
              color: theme.palette.primary.main,
              fontWeight: 300,
              '&:hover': {
                opacity: 0.8,
              },
            }}>
            {maxChips === DEFAULT_MAX_CHIPS ? `View more (${settings.addedUsers.length - maxChips})` : 'Collapse'}
          </Typography>
        )}
      </Stack>

      <Divider />

      {/* disbaled until reccuring export is enabled */}
      {false && (
        <Stack direction='row' gap={2}>
          <Switch
            checked={settings.isRecurring}
            onChange={() => {
              updateSettings({ isRecurring: !settings.isRecurring });
            }}
          />
          <Typography>Make this a recurring export</Typography>
        </Stack>
      )}
      {settings.isRecurring && (
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography>Repeat every</Typography>
          <Select
            options={timePeriods}
            value={settings.recurringPeriod}
            sx={styles.select}
            onChange={period => updateSettings({ recurringPeriod: period })}
          />
          {settings.recurringPeriod === 'MONTH' && (
            <>
              <Typography>on the</Typography>
              <Select
                options={monthDays}
                value={settings.recurringMonthDay}
                sx={styles.select}
                onChange={day => updateSettings({ recurringMonthDay: day })}
              />
              <Typography>of the month, </Typography>
            </>
          )}

          {settings.recurringPeriod === 'WEEK' && (
            <>
              <Typography>on</Typography>
              <Select
                options={weekDays}
                value={settings.recurringWeekDay}
                sx={styles.select}
                onChange={day => updateSettings({ recurringWeekDay: day })}
              />
            </>
          )}

          <Typography>at</Typography>

          {/* TODO: Add time selector when recurring export is enabled */}
        </Stack>
      )}

      <Stack gap='4px'>
        <Typography color='textSecondary'>Report Name</Typography>
        <TextField
          value={fileName}
          onChange={e => handleChangeFileName(e.target.value)}
          placeholder='Report Name'
          sx={{
            borderRadius: 1,
            backgroundColor: theme.palette.background.default,
            borderColor: theme.palette.border.main,
            color: theme.palette.text.primary,
            textTransform: 'none',
            input: { paddingY: 1 },
          }}
        />
      </Stack>

      {/* disabled until pdf is enabled */}
      {false && (
        <Stack direction='row' gap={2}>
          {FORMATS.map(format => (
            <Stack key={format} direction='row' alignItems='center'>
              <FormControlLabel
                control={<Checkbox checked={settings.formats[format]} onChange={handleSelectedFormats} name={format} />}
                label={format}
                sx={{ marginRight: '8px' }}
              />
              <InfoOutlined fontSize='small' color='info' />
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
};

export default ExportSettings;
